import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  MessageSquarePlus,
  Bug,
  Lightbulb,
  Palette,
  HelpCircle,
  MoreHorizontal,
  ChevronDown,
  Send,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../services/api';

/**
 * Platformfeedback ("verbeterpunten"). Woonde eerder in het Mijn Belagom-menu;
 * staat nu in de zwevende support-widget zodat de kantoorinstellingen van de
 * professional puur bedrijfsmatig blijven.
 */
interface FeedbackItem {
  id: number;
  categorie: string;
  onderdeel?: string;
  prioriteit: string;
  titel: string;
  beschrijving: string;
  status: string;
  admin_reactie?: string;
  created_at: string;
}

export const PlatformFeedbackPanel: React.FC = () => {
  const { t, i18n } = useTranslation('belagom');

  const CATEGORIE_OPTIONS = [
    { value: 'bug',             label: t('constant.feedbackCategory.bug'),            icon: Bug,            color: 'text-red-500',    bg: 'bg-red-50' },
    { value: 'feature_request', label: t('constant.feedbackCategory.featureRequest'), icon: Lightbulb,      color: 'text-yellow-500', bg: 'bg-yellow-50' },
    { value: 'ux_verbetering',  label: t('constant.feedbackCategory.uxImprovement'),  icon: Palette,        color: 'text-purple-500', bg: 'bg-purple-50' },
    { value: 'vraag',           label: t('constant.feedbackCategory.question'),       icon: HelpCircle,     color: 'text-blue-500',   bg: 'bg-blue-50' },
    { value: 'overig',          label: t('constant.feedbackCategory.other'),          icon: MoreHorizontal, color: 'text-gray-500',   bg: 'bg-gray-50' },
  ] as const;

  const ONDERDEEL_OPTIONS = [
    { value: '',               label: t('constant.feedbackSection.general') },
    { value: 'dashboard',      label: t('constant.feedbackSection.dashboard') },
    { value: 'profiel',        label: t('constant.feedbackSection.profiel') },
    { value: 'dossiers',       label: t('constant.feedbackSection.dossiers') },
    { value: 'facturen',       label: t('constant.feedbackSection.facturen') },
    { value: 'community',      label: t('constant.feedbackSection.community') },
    { value: 'connect_meet',   label: t('constant.feedbackSection.connect_meet') },
    { value: 'opleidingen',    label: t('constant.feedbackSection.opleidingen') },
    { value: 'intervisie',     label: t('constant.feedbackSection.intervisie') },
    { value: 'berichten',      label: t('constant.feedbackSection.berichten') },
    { value: 'mijn_belagom',   label: t('constant.feedbackSection.mijn_belagom') },
    { value: 'overig',         label: t('constant.feedbackSection.overig') },
  ];

  const PRIORITEIT_OPTIONS = [
    { value: 'laag',    label: t('constant.feedbackPriority.low'),    description: t('constant.feedbackPriority.lowDesc') },
    { value: 'normaal', label: t('constant.feedbackPriority.normal'), description: t('constant.feedbackPriority.normalDesc') },
    { value: 'hoog',    label: t('constant.feedbackPriority.high'),   description: t('constant.feedbackPriority.highDesc') },
  ] as const;

  const STATUS_LABELS: Record<string, { label: string; color: string }> = {
    ingediend:      { label: t('constant.feedbackStatus.ingediend'),      color: 'bg-blue-100 text-blue-700' },
    in_behandeling: { label: t('constant.feedbackStatus.in_behandeling'), color: 'bg-yellow-100 text-yellow-700' },
    opgelost:       { label: t('constant.feedbackStatus.opgelost'),       color: 'bg-blue-100 text-blue-700' },
    afgewezen:      { label: t('constant.feedbackStatus.afgewezen'),      color: 'bg-gray-100 text-gray-500' },
  };

  const queryClientFb = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [categorie, setCategorie] = useState('');
  const [onderdeel, setOnderdeel] = useState('');
  const [prioriteit, setPrioriteit] = useState('normaal');
  const [titel, setTitel] = useState('');
  const [beschrijving, setBeschrijving] = useState('');

  const { data: items = [], isLoading: fbLoading } = useQuery<FeedbackItem[]>({
    queryKey: ['platform-feedback'],
    queryFn: async () => {
      const res = await api.get('/professional/platform-feedback');
      return res.data?.data ?? [];
    },
  });

  const mutation = useMutation({
    mutationFn: async (payload: {
      categorie: string; onderdeel?: string; prioriteit: string; titel: string; beschrijving: string;
    }) => {
      const res = await api.post('/professional/platform-feedback', payload);
      return res.data;
    },
    onSuccess: () => {
      toast.success(t('toast.feedbackThanks'));
      queryClientFb.invalidateQueries({ queryKey: ['platform-feedback'] });
      setCategorie('');
      setOnderdeel('');
      setPrioriteit('normaal');
      setTitel('');
      setBeschrijving('');
      setShowForm(false);
    },
    onError: () => {
      toast.error(t('toast.errorRetry'));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!categorie) { toast.error(t('validation.selectCategory')); return; }
    if (titel.trim().length < 5) { toast.error(t('validation.titleTooShort')); return; }
    if (beschrijving.trim().length < 10) { toast.error(t('validation.descriptionTooShort')); return; }
    mutation.mutate({
      categorie,
      onderdeel: onderdeel || undefined,
      prioriteit,
      titel: titel.trim(),
      beschrijving: beschrijving.trim(),
    });
  };

  const fmtDateFb = (d: string) =>
    new Date(d).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="max-w-2xl space-y-6">
      {/* Intro */}
      <div className="rounded-xl border border-blue-100 bg-blue-50 p-5">
        <div className="flex items-start gap-3">
          <MessageSquarePlus size={20} className="text-[#0B1F3A] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-[#0B1F3A] mb-1">{t('section.improvePlatform')}</p>
            <p className="text-sm text-blue-800 leading-relaxed">
              {t('info.feedbackHint')}
            </p>
          </div>
        </div>
      </div>

      {/* Toggle form button */}
      {!showForm && (
        <button
          type="button"
          onClick={() => setShowForm(true)}
          className="w-full flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#0B1F3A] text-[#0B1F3A] font-semibold py-4 hover:bg-blue-50 transition-colors"
        >
          <MessageSquarePlus size={18} />
          {t('button.submitFeedback')}
        </button>
      )}

      {/* Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="rounded-xl border border-gray-200 bg-white p-6 space-y-5 shadow-sm">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-semibold text-gray-900">{t('button.submitFeedback')}</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-xs text-gray-400 hover:text-gray-600">
              {t('button.cancel')}
            </button>
          </div>

          {/* Categorie */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('label.category')} <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {CATEGORIE_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const selected = categorie === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setCategorie(opt.value)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                      selected
                        ? 'border-[#0B1F3A] bg-[#0B1F3A] text-white shadow-sm'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <Icon size={15} className={selected ? 'text-white' : opt.color} />
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Onderdeel */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {t('label.platformSection')}
            </label>
            <div className="relative">
              <select
                value={onderdeel}
                onChange={(e) => setOnderdeel(e.target.value)}
                className="w-full appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2.5 pr-8 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#0B1F3A] focus:border-transparent"
              >
                {ONDERDEEL_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <ChevronDown size={15} className="absolute right-2.5 top-3 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Prioriteit */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('label.impact')} <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              {PRIORITEIT_OPTIONS.map((opt) => {
                const selected = prioriteit === opt.value;
                const colorMap: Record<string, string> = {
                  laag:    'border-blue-400 bg-blue-50 text-blue-700',
                  normaal: 'border-yellow-400 bg-yellow-50 text-yellow-700',
                  hoog:    'border-red-400 bg-red-50 text-red-700',
                };
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setPrioriteit(opt.value)}
                    title={opt.description}
                    className={`flex-1 px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                      selected ? colorMap[opt.value] : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <p className="mt-1.5 text-xs text-gray-400">
              {PRIORITEIT_OPTIONS.find((o) => o.value === prioriteit)?.description}
            </p>
          </div>

          {/* Titel */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {t('label.title')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={titel}
              onChange={(e) => setTitel(e.target.value)}
              placeholder={t('placeholder.feedbackTitle')}
              maxLength={200}
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0B1F3A] focus:border-transparent"
            />
            <p className="mt-1 text-xs text-gray-400 text-right">{titel.length}/200</p>
          </div>

          {/* Beschrijving */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {t('label.description')} <span className="text-red-500">*</span>
            </label>
            <textarea
              value={beschrijving}
              onChange={(e) => setBeschrijving(e.target.value)}
              placeholder={
                categorie === 'bug'
                  ? t('placeholder.bugDescription')
                  : categorie === 'feature_request'
                  ? t('placeholder.featureDescription')
                  : t('placeholder.feedbackDescription')
              }
              rows={5}
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0B1F3A] focus:border-transparent resize-none"
            />
          </div>

          {/* Submit */}
          <div className="flex justify-end pt-1">
            <button
              type="submit"
              disabled={mutation.isPending}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#0B1F3A] text-white text-sm font-semibold hover:bg-[#1A3459] disabled:opacity-60 transition-colors"
            >
              <Send size={15} />
              {mutation.isPending ? t('button.sending') : t('button.submit')}
            </button>
          </div>
        </form>
      )}

      {/* Submitted items */}
      <div>
        <h3 className="text-sm font-semibold text-gray-600 mb-3">{t('label.previousFeedback', { count: items.length })}</h3>

        {fbLoading ? (
          <p className="text-sm text-gray-400">{t('common:common.loading')}</p>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-400">
            {t('info.noFeedback')}
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item) => {
              const cat = CATEGORIE_OPTIONS.find((c) => c.value === item.categorie);
              const CatIcon = cat?.icon ?? MoreHorizontal;
              const statusInfo = STATUS_LABELS[item.status] ?? STATUS_LABELS['ingediend'];
              return (
                <div key={item.id} className="rounded-xl border border-gray-200 bg-white p-4 space-y-2 hover:shadow-sm transition-shadow">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${cat?.bg ?? 'bg-gray-50'}`}>
                        <CatIcon size={14} className={cat?.color ?? 'text-gray-500'} />
                      </span>
                      <p className="text-sm font-semibold text-gray-900 truncate">{item.titel}</p>
                    </div>
                    <span className={`flex-shrink-0 text-xs font-medium px-2.5 py-1 rounded-full ${statusInfo.color}`}>
                      {statusInfo.label}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2 pl-9">{item.beschrijving}</p>
                  <div className="flex items-center gap-3 pl-9 text-xs text-gray-400">
                    {item.onderdeel && (
                      <span>{ONDERDEEL_OPTIONS.find((o) => o.value === item.onderdeel)?.label ?? item.onderdeel}</span>
                    )}
                    <span>{fmtDateFb(item.created_at)}</span>
                  </div>
                  {item.admin_reactie && (
                    <div className="ml-9 mt-2 rounded-lg bg-blue-50 border border-blue-200 px-3 py-2.5 flex items-start gap-2">
                      <AlertCircle size={14} className="text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs font-semibold text-blue-700 mb-0.5">{t('label.belagomResponse')}</p>
                        <p className="text-xs text-blue-800">{item.admin_reactie}</p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
